import { useEffect, useRef, useState } from 'react';
import { Plus, Eye, Save, Trash2, Image } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';
import { Term } from '../../components/admin/AdminGlossary';

const ZODIAC_SIGNS = ['Áries','Touro','Gêmeos','Câncer','Leão','Virgem','Libra','Escorpião','Sagitário','Capricórnio','Aquário','Peixes'];

interface Article {
  id: string; title: string; slug: string; meta_description: string | null;
  content: string | null; cover_image_url: string | null;
  status: 'draft' | 'published'; created_at: string; author_id: string | null;
}

export function AdminContentPage() {
  const { profile }    = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [editing,  setEditing]  = useState<Article | null>(null);
  const [saving,   setSaving]   = useState(false);

  // Editor state (synced from `editing`)
  const [title,      setTitle]      = useState('');
  const [slug,       setSlug]       = useState('');
  const [metaDesc,   setMetaDesc]   = useState('');
  const [content,    setContent]    = useState('');
  const [coverUrl,   setCoverUrl]   = useState('');
  const [status,     setStatus]     = useState<'draft' | 'published'>('draft');
  const [tags,       setTags]       = useState('');
  const [uploading,  setUploading]  = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => { loadArticles(); }, []);

  async function loadArticles() {
    setLoading(true);
    const { data } = await supabase
      .from('blog_articles')
      .select('id, title, slug, meta_description, content, cover_image_url, status, created_at, author_id')
      .order('created_at', { ascending: false });
    setArticles((data ?? []) as Article[]);
    setLoading(false);
  }

  async function uploadCover(file: File) {
    setUploading(true);
    const ext  = file.name.split('.').pop() ?? 'jpg';
    const path = `covers/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('blog-images').upload(path, file, { upsert: true });
    if (!error) {
      const { data: pub } = supabase.storage.from('blog-images').getPublicUrl(path);
      setCoverUrl(pub.publicUrl);
    }
    setUploading(false);
  }

  function handleTitleChange(v: string) {
    setTitle(v);
    setSlug(v.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''));
  }

  function openEditor(article?: Article) {
    if (article) {
      setEditing(article);
      setTitle(article.title);
      setSlug(article.slug);
      setMetaDesc(article.meta_description ?? '');
      setContent(article.content ?? '');
      setCoverUrl(article.cover_image_url ?? '');
      setStatus(article.status);
      setTags('');
    } else {
      setEditing(null);
      setTitle(''); setSlug(''); setMetaDesc(''); setContent(''); setCoverUrl(''); setStatus('draft'); setTags('');
    }
  }

  async function saveArticle() {
    if (!title.trim()) return;
    setSaving(true);
    const payload = {
      title, slug, meta_description: metaDesc || null,
      content: content || null, cover_image_url: coverUrl || null,
      status, author_id: profile?.id,
    };

    if (editing?.id) {
      await supabase.from('blog_articles').update(payload).eq('id', editing.id);
    } else {
      await supabase.from('blog_articles').insert(payload);
    }

    setSaving(false);
    setEditing(undefined as unknown as Article);
    loadArticles();
  }

  async function deleteArticle(id: string) {
    if (!window.confirm('Excluir este artigo permanentemente?')) return;
    await supabase.from('blog_articles').delete().eq('id', id);
    loadArticles();
  }

  async function toggleStatus(article: Article) {
    const newStatus = article.status === 'published' ? 'draft' : 'published';
    await supabase.from('blog_articles').update({ status: newStatus }).eq('id', article.id);
    setArticles(prev => prev.map(a => a.id === article.id ? { ...a, status: newStatus } : a));
  }

  // Editor is open if editing is explicitly set (null = create new, Article = edit existing)
  const editorOpen = editing !== undefined;

  return (
    <div>
      <AdminPageHeader
        title="Conteúdo"
        objective="Gerencie artigos do blog, páginas de signos e compatibilidade entre signos. Conteúdo publicado aparece no site público e é indexado pelo Google (SEO, otimização para buscadores). Artigos em 'Rascunho' só são visíveis para administradores."
      />

      <div style={{ display: 'flex', gap: '0.35rem', marginBottom: '1.5rem' }}>
        {['Blog', 'Signos', 'Compatibilidade'].map((t, i) => (
          <button key={t} onClick={() => { setActiveTab(i); setEditing(undefined as unknown as Article); }}
            className={activeTab === i ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
            {t}
          </button>
        ))}
      </div>

      {/* Blog tab */}
      {activeTab === 0 && (
        <>
          {editorOpen ? (
            <div className="card animate-fade-in">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
                <h2 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: 0 }}>
                  {editing ? `Editando: ${editing.title}` : 'Novo artigo'}
                </h2>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditing(undefined as unknown as Article)}>✕ Fechar</button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Título</label>
                  <input type="text" className="form-input" value={title} onChange={e => handleTitleChange(e.target.value)} placeholder="Como ler seu mapa astral" />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Slug</label>
                  <input type="text" className="form-input" value={slug} onChange={e => setSlug(e.target.value)} style={{ fontFamily: 'monospace', fontSize: '13px' }} />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label"><Term k="SEO">Meta descrição (SEO)</Term> <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({metaDesc.length}/160)</span></label>
                <textarea className="form-textarea" value={metaDesc} onChange={e => setMetaDesc(e.target.value.slice(0, 160))} rows={2} placeholder="Breve descrição para SEO..." />
              </div>

              <div className="form-group">
                <label className="form-label">Campanha / Tag</label>
                <input
                  type="text"
                  className="form-input"
                  value={tags}
                  onChange={e => setTags(e.target.value)}
                  placeholder="astrologia, mapa astral, amor"
                />
                <p className="form-hint">Tags separadas por vírgula. Usadas para segmentação e campanhas.</p>
              </div>

              <div className="form-group">
                <label className="form-label">Imagem de capa</label>
                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files?.[0]; if (f) uploadCover(f); }} />
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="url"
                    className="form-input"
                    value={coverUrl}
                    onChange={e => setCoverUrl(e.target.value)}
                    placeholder="https://... ou use o botão para upload"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', whiteSpace: 'nowrap' }}
                  >
                    <Image size={14} /> {uploading ? 'Enviando...' : 'Upload'}
                  </button>
                  {coverUrl && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCoverUrl('')} title="Remover">✕</button>
                  )}
                </div>
                {coverUrl && (
                  <img src={coverUrl} alt="Capa" style={{ marginTop: '0.5rem', maxHeight: '120px', borderRadius: '6px', objectFit: 'cover', border: '1px solid var(--border-subtle)' }} />
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Conteúdo (<span title="Markdown: linguagem de formatação simples, use # para títulos, **texto** para negrito, *texto* para itálico." style={{ borderBottom: '1px dashed var(--petrol)', cursor: 'help' }}>Markdown</span>)</label>
                <textarea className="form-textarea" value={content} onChange={e => setContent(e.target.value)} rows={16}
                  placeholder={'# Título\n\nPrimeiro parágrafo do artigo...\n\n## Seção 2\n\nMais conteúdo aqui.'}
                  style={{ fontFamily: 'monospace', fontSize: '13px', lineHeight: 1.6 }} />
                <p className="form-hint">💡 Use # para títulos, **negrito**, *itálico*, - para listas. Imagens: ![Descrição](URL)</p>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <select className="form-select" value={status} onChange={e => setStatus(e.target.value as 'draft' | 'published')} style={{ maxWidth: '180px' }}>
                  <option value="draft">Rascunho</option>
                  <option value="published">Publicado</option>
                </select>
                <button className="btn btn-primary btn-sm" onClick={saveArticle} disabled={saving || !title.trim()}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Save size={14} /> {saving ? 'Salvando...' : 'Salvar'}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditing(undefined as unknown as Article)}>Cancelar</button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{articles.length} artigos</span>
                  <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', backgroundColor: 'rgba(74,222,128,0.12)', color: '#4ade80', fontWeight: 600 }}>
                    ✓ {articles.filter(a => a.status === 'published').length} publicados
                  </span>
                  <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', backgroundColor: 'rgba(251,191,36,0.12)', color: '#fbbf24', fontWeight: 600 }}>
                    ✎ {articles.filter(a => a.status === 'draft').length} rascunho
                  </span>
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => openEditor()}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Plus size={15} /> Novo artigo
                </button>
              </div>

              {loading ? (
                <div className="card shimmer" style={{ height: '200px' }} />
              ) : (
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <table className="data-table" style={{ width: '100%' }}>
                    <thead>
                      <tr><th>Título</th><th>Slug</th><th>Status</th><th>Data</th><th></th></tr>
                    </thead>
                    <tbody>
                      {articles.map(a => (
                        <tr key={a.id}>
                          <td style={{ fontWeight: 600, color: 'var(--text-primary)', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: '12px', color: 'var(--text-muted)' }}>{a.slug}</td>
                          <td>
                            <button onClick={() => toggleStatus(a)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                              <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px',
                                backgroundColor: a.status === 'published' ? 'rgba(74,222,128,0.12)' : 'rgba(251,191,36,0.12)',
                                color: a.status === 'published' ? '#4ade80' : '#fbbf24' }}>
                                {a.status === 'published' ? 'Publicado' : 'Rascunho'}
                              </span>
                            </button>
                          </td>
                          <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                            {new Date(a.created_at).toLocaleDateString('pt-BR')}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                              <button className="btn btn-ghost btn-sm" onClick={() => openEditor(a)} title="Editar">
                                <Eye size={14} />
                              </button>
                              <button className="btn btn-ghost btn-sm" onClick={() => deleteArticle(a.id)} title="Excluir"
                                style={{ color: '#f87171' }}>
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {articles.length === 0 && (
                        <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: '2rem' }}>
                          Nenhum artigo criado ainda
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Signos tab */}
      {activeTab === 1 && (
        <div className="card">
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '1rem' }}>12 páginas de signos disponíveis no <Term k="SEO">SEO</Term></p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.5rem' }}>
            {ZODIAC_SIGNS.map(sign => (
              <div key={sign} style={{ padding: '0.75rem', backgroundColor: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>{sign}</span>
                <a href={`/signos/${sign.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')}`} target="_blank" rel="noreferrer">
                  <Eye size={14} color="var(--text-muted)" />
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Compatibilidade tab */}
      {activeTab === 2 && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div>
              <p style={{ color: 'var(--text-primary)', fontWeight: 600, marginBottom: '0.25rem', margin: '0 0 0.25rem' }}>144 páginas de compatibilidade</p>
              <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>Todas as combinações de 12 × 12 signos disponíveis para SEO</p>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '4px' }}>
            {ZODIAC_SIGNS.slice(0, 6).map(s1 => ZODIAC_SIGNS.slice(0, 6).map(s2 => (
              <div key={`${s1}-${s2}`} style={{ padding: '4px 6px', backgroundColor: 'rgba(74,222,128,0.08)', borderRadius: '4px', fontSize: '10px', color: 'var(--text-muted)', textAlign: 'center' }}>
                {s1.slice(0, 3)} × {s2.slice(0, 3)}
              </div>
            )))}
          </div>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginTop: '0.5rem' }}>Mostrando 36 de 144</p>
        </div>
      )}
    </div>
  );
}
